import React from 'react';
import PropTypes from 'prop-types';
import './Checkbox.scss';
import { createClassList } from '../../utils/dom/class';

/**
 *  For further guidance on the Checkbox component, please visit the
   <a target="_blank" href="http://hqcontent.edwardjones.com/GUI/guisite/axiom-ds/components/form/checkbox" class="axiom-link"> AXIOM</a>
    documentation site.
    <br/>
    <br/>
 * The 'hideLabel' component will allow for designs where a Checkbox
 * does not need a visual label, such as within the context of a table.
 * However, the 'label' prop is still required for Accessibility compliance. Do
 * not pass an empty string, as this will not meet Accessibility standards.
 * A label is needed for screen-readers to communicate the purpose of the
 * Checkbox to non-visual users who will not be able to understand the purpose
 * from the visual context.
*/

const Checkbox = ({
  id,
  name,
  label,
  checked,
  disabled,
  onChange,
  inputRef,
  hideLabel,
  indeterminate,
  attributes,
  condensed,
  classes,
}) => {
  const defaultClasses = ['axiom-checkbox-set checkbox-set'];
  if (indeterminate === true) {
    defaultClasses.push('indeterminate');
  }
  if (condensed === true) {
    defaultClasses.push('condensed');
  }

  return (
    <div
      key={label}
      data-testid='checkbox-input-container'
      className={createClassList({
        default: defaultClasses,
        custom: classes,
        required: [],
      })}
    >
      <input
        type='checkbox'
        id={id}
        name={name}
        value={label}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        checked={checked}
        aria-checked={
          // eslint-disable-next-line no-nested-ternary
          indeterminate ? 'mixed' : checked ? 'true' : 'false'
        }
        data-testid='checkbox-input'
        ref={inputRef}
        {...attributes}
      />
      <label htmlFor={id} className='axiom-input-label--checkbox'>
        <span className='axiom-checkbox--box' aria-hidden='true'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='22px'
            height='22px'
            viewBox='0 0 22 22'
          >
            {indeterminate ? (
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M15 10C15.8284 10 16.5 10.6716 16.5 11.5C16.5 12.2797 15.9051 12.9204 15.1445 12.9931L15 13H7C6.17157 13 5.5 12.3284 5.5 11.5C5.5 10.7203 6.09489 10.0796 6.85554 10.0069L7 10H15Z'
                stroke='none'
              />
            ) : (
              <path
                d='M4.40002 12.5345L7.09392 16.3625C7.31392 16.676 7.65492 16.8641 8.02012 16.874C8.38642 16.885 8.73622 16.7178 8.97162 16.4197L17.6 5.5'
                strokeWidth='3'
                strokeLinecap='round'
                strokeLinejoin='round'
                fill='none'
              />
            )}
          </svg>
        </span>
        <span className={hideLabel ? 'screen-reader-only' : null}>
          {label}
        </span>
      </label>
    </div>
  );
};

Checkbox.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  onChange: PropTypes.func.isRequired,
  hideLabel: PropTypes.bool,
  indeterminate: PropTypes.bool,
  attributes: PropTypes.objectOf(PropTypes.any),
  condensed: PropTypes.bool,
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

Checkbox.defaultProps = {
  disabled: false,
  inputRef: undefined,
  hideLabel: false,
  indeterminate: false,
  attributes: {},
  condensed: false,
  classes: [],
};

export default Checkbox;
